import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useBlogStore } from '../stores/blogStore';
import { useProfileStore } from '../stores/profileStore';
import { Button } from '../components/common/Button';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { BlogPost, BlogLocale, BlogStatus } from '../types/blog';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Save,
  Trash2,
  Image as ImageIcon,
  Globe,
  Search,
  Plus,
  Info,
  Calendar,
  Clock,
  ChevronDown,
  X,
  User,
  Loader2,
  Eye,
  Columns2,
  Pencil,
  ExternalLink,
  Languages,
  Images,
} from 'lucide-react';
import { InputGroup } from '../components/common/InputGroup';
import RichTextEditor, { type RichTextEditorHandle } from '../components/admin/blog/RichTextEditor';
import BlogImageLibraryModal from '../components/admin/blog/BlogImageLibraryModal';
import {
  get_website_article_path,
  get_website_article_preview_url,
  get_website_base_url,
  type WebsiteLocale,
} from '../lib/site_config';
import { sanitize_blog_iframe_html } from '../lib/trustedIframeOrigins';

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }

  return fallback;
};

type preview_mode = 'edit' | 'split' | 'preview';

const preview_mode_button_classes = (is_active: boolean): string =>
  [
    'rounded-full px-4 py-2 text-sm font-bold transition-all',
    is_active
      ? 'bg-primary text-white shadow-[0_16px_30px_rgba(0,116,117,0.18)]'
      : 'bg-white text-secondary/75 hover:bg-secondary/5 hover:text-secondary',
  ].join(' ');

const BlogEditor: React.FC = () => {
  const { t } = useTranslation('admin');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    getPostById,
    getPostIdByLocaleInGroup,
    createPost,
    updatePost,
    deletePost,
    uploadImage,
    fetchCategories,
    fetchTags,
    fetchAuthors,
    categories,
    tags,
    authors,
  } = useBlogStore();
  const { profile } = useProfileStore();

  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [inlineImageLoading, setInlineImageLoading] = useState(false);
  const [post, setPost] = useState<Partial<BlogPost>>({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    status: 'draft',
    seo_title: '',
    seo_description: '',
    seo_keywords: [],
    ai_summary: '',
    published_at: new Date().toISOString(),
  });

  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isSeoExpanded, setIsSeoExpanded] = useState(false);
  const [previewMode, setPreviewMode] = useState<preview_mode>('split');
  const [siblingPostId, setSiblingPostId] = useState<string | null>(null);
  const richTextEditorRef = useRef<RichTextEditorHandle>(null);
  const [imageLibraryOpen, setImageLibraryOpen] = useState(false);
  const [imageLibraryTarget, setImageLibraryTarget] = useState<'featured' | 'inline'>('inline');

  const openImageLibrary = (target: 'featured' | 'inline') => {
    setImageLibraryTarget(target);
    setImageLibraryOpen(true);
  };

  const handleImageLibraryPick = (url: string) => {
    if (imageLibraryTarget === 'featured') {
      setPost(prev => ({ ...prev, featured_image_url: url }));
    } else {
      richTextEditorRef.current?.insertImage(url);
    }
    setImageLibraryOpen(false);
  };

  useEffect(() => {
    fetchCategories();
    fetchTags();
    fetchAuthors();

    if (id && id !== 'new') {
      setLoading(true);
      getPostById(id).then(data => {
        if (data) {
          setPost(data);
          setSelectedCategoryIds(data.categories?.map(c => c.id) || []);
          setSelectedTagIds(data.tags?.map(t => t.id) || []);
        }
        setLoading(false);
      });
    } else if (id === 'new' && profile) {
      setPost(prev => ({ ...prev, author_id: profile.id }));
    }
  }, [id, getPostById, fetchCategories, fetchTags, fetchAuthors, profile]);

  useEffect(() => {
    if (id !== 'new' || !profile) return;
    setPost(prev => {
      const loc = searchParams.get('locale') === 'en' ? 'en' : 'fr';
      const tgParam = searchParams.get('translation_group');
      const translation_group_id = tgParam ?? prev.translation_group_id ?? crypto.randomUUID();
      const rest = { ...prev };
      delete rest.id;
      return {
        ...rest,
        author_id: prev.author_id ?? profile.id,
        locale: loc,
        translation_group_id,
      };
    });
  }, [id, profile, searchParams]);

  useEffect(() => {
    if (!post.translation_group_id || !post.locale) {
      setSiblingPostId(null);
      return;
    }
    const otherLocale: BlogLocale = post.locale === 'fr' ? 'en' : 'fr';
    getPostIdByLocaleInGroup(post.translation_group_id, otherLocale).then(setSiblingPostId);
  }, [post.translation_group_id, post.locale, getPostIdByLocaleInGroup]);

  const handleTitleChange = (val: string) => {
    const slug = val
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

    setPost({ ...post, title: val, slug });
  };

  const handleSave = async () => {
    if (!post.title || !post.slug) {
      toast.error(t('blogAdmin.required_fields'));
      return;
    }

    setLoading(true);
    try {
      if (id && id !== 'new') {
        await updatePost(id, post, selectedCategoryIds, selectedTagIds);
        toast.success(t('blogAdmin.update_success'));
      } else {
        // Ensure category field is set for DB NOT NULL constraint
        const postData = {
          ...post,
          locale: (post.locale ?? 'fr') as BlogLocale,
          translation_group_id: post.translation_group_id ?? crypto.randomUUID(),
          category:
            post.category ||
            categories.find(c => selectedCategoryIds.includes(c.id))?.name ||
            'Général',
        };
        const newPost = await createPost(postData, selectedCategoryIds, selectedTagIds);
        if (newPost) {
          toast.success(t('blogAdmin.create_success'));
          navigate(`/admin/blog/${newPost.id}`);
        }
      }
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, t('blogAdmin.save_error'));
      toast.error(`${t('blogAdmin.save_error')}: ${errorMessage}`);
      console.error('Save error details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      if (id) {
        await deletePost(id);
        toast.success(t('blogAdmin.delete_success'));
        navigate('/admin/blog');
      }
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, 'Unknown error');
      toast.error(`${t('blogAdmin.save_error')}: ${errorMessage}`);
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageLoading(true);
      const loadingToast = toast.info(
        t('blogAdmin.uploading_image') || "Téléchargement de l'image...",
        { autoClose: false }
      );
      try {
        const url = await uploadImage(file);
        if (url) {
          setPost(prev => ({ ...prev, featured_image_url: url }));
          toast.dismiss(loadingToast);
          toast.success(t('blogAdmin.image_upload_success'));
        }
      } catch (error: unknown) {
        toast.dismiss(loadingToast);
        toast.error(
          `${t('blogAdmin.image_upload_error') || 'Erreur lors du téléchargement'}: ${getErrorMessage(error, 'Unknown error')}`
        );
      } finally {
        setImageLoading(false);
      }
    }
  };

  const handleInlineImageUpload = async (file: File): Promise<string | null> => {
    setInlineImageLoading(true);
    const loadingToast = toast.info(
      t('blogAdmin.uploading_image') || "Téléchargement de l'image...",
      { autoClose: false }
    );

    try {
      const url = await uploadImage(file);
      if (url) {
        toast.dismiss(loadingToast);
        toast.success(t('blogAdmin.image_upload_success'));
        return url;
      }
      toast.dismiss(loadingToast);
      return null;
    } catch (error: unknown) {
      toast.dismiss(loadingToast);
      toast.error(
        `${t('blogAdmin.image_upload_error') || 'Erreur lors du téléchargement'}: ${getErrorMessage(error, 'Unknown error')}`
      );
      return null;
    } finally {
      setInlineImageLoading(false);
    }
  };

  const toggleCategory = (catId: string) => {
    setSelectedCategoryIds(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const handleQuickCategory = async () => {
    const name = window.prompt(t('blogAdmin.category_name_prompt'));
    if (name) {
      const slug = name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]/g, '');
      const newCat = await useBlogStore.getState().createCategory({ name, slug });
      if (newCat) {
        setSelectedCategoryIds(prev => [...prev, newCat.id]);
        toast.success(t('blogAdmin.category_created'));
      }
    }
  };

  const handleQuickTag = async (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]/g, '');
    const newTag = await useBlogStore.getState().createTag({ name, slug });
    if (newTag) {
      setSelectedTagIds(prev => [...prev, newTag.id]);
      toast.success(t('blogAdmin.tag_created'));
    }
  };

  /** Aligns with marketing site routes (`/ressources/...` for fr, `/en/ressources/...` for en). */
  const articleLocale: WebsiteLocale = post.locale === 'en' ? 'en' : 'fr';
  const slugForPublicUrl = (post.slug ?? '').trim();
  const publicArticleUrl = `${get_website_base_url()}${get_website_article_path(articleLocale, slugForPublicUrl || '...')}`;
  /** Only after persist: `/admin/blog/new` has no `id` param; `:id` is UUID after first save. */
  const canOpenWebsitePreview = Boolean(id && id !== 'new' && slugForPublicUrl);
  const previewUrl = canOpenWebsitePreview
    ? get_website_article_preview_url(articleLocale, slugForPublicUrl)
    : '';
  const otherLocale: BlogLocale = post.locale === 'en' ? 'fr' : 'en';

  if (loading && id !== 'new')
    return <div className="p-8 text-center text-gray-500">{t('loading')}</div>;

  const renderPreviewPanel = () => {
    const rawContent = post.content || `<p>${t('blogAdmin.preview_empty_content')}</p>`;
    const previewContent = sanitize_blog_iframe_html(rawContent);

    return (
      <div className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm md:p-8">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary/70">
              {t('blogAdmin.preview_label')}
            </p>
            <h3 className="mt-2 text-lg font-bold text-secondary">
              {post.title || t('blogAdmin.preview_empty_title')}
            </h3>
          </div>
          <span className="rounded-full bg-secondary/5 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-secondary/60">
            {post.status === 'published'
              ? t('status.published')
              : post.status === 'scheduled'
                ? t('status.scheduled')
                : t('status.draft')}
          </span>
        </div>

        {post.featured_image_url && (
          <div className="mb-6 overflow-hidden rounded-[24px] border border-secondary/10 bg-gray-50">
            <img
              src={post.featured_image_url}
              alt={post.title || 'Preview'}
              className="aspect-[1200/630] w-full object-cover"
            />
          </div>
        )}

        <div className="space-y-4">
          {post.excerpt ? (
            <p className="text-base leading-7 text-secondary/75">{post.excerpt}</p>
          ) : (
            <p className="text-sm italic text-secondary/45">
              {t('blogAdmin.preview_empty_excerpt')}
            </p>
          )}

          <article
            className={[
              'rounded-[24px] bg-[#F8FBFB] p-5 text-secondary/85',
              '[&_h1]:mb-4 [&_h1]:text-4xl [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:text-secondary',
              '[&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-secondary',
              '[&_h3]:mb-3 [&_h3]:mt-6 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-secondary',
              '[&_p]:mb-4 [&_p]:leading-7',
              '[&_ul]:mb-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5',
              '[&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-5',
              '[&_blockquote]:my-5 [&_blockquote]:rounded-r-2xl [&_blockquote]:border-l-4 [&_blockquote]:border-primary/35 [&_blockquote]:bg-white [&_blockquote]:px-4 [&_blockquote]:py-3 [&_blockquote]:italic',
              '[&_pre]:overflow-x-auto [&_pre]:rounded-2xl [&_pre]:bg-secondary [&_pre]:p-4 [&_pre]:text-sm [&_pre]:text-white',
              '[&_code]:rounded [&_code]:bg-white/80 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[0.95em]',
              '[&_iframe]:my-6 [&_iframe]:aspect-video [&_iframe]:h-auto [&_iframe]:w-full [&_iframe]:rounded-2xl [&_iframe]:border [&_iframe]:border-secondary/15 [&_iframe]:bg-black/5',
            ].join(' ')}
            dangerouslySetInnerHTML={{
              __html: previewContent,
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
      {/* Writing Guide (Readme) */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-4 mb-8 flex gap-4 items-start shadow-sm">
        <div className="p-3 bg-blue-100 rounded-xl text-blue-600">
          <Info size={24} />
        </div>
        <div>
          <h4 className="font-bold text-blue-900 mb-1">{t('blogAdmin.writing_guide_title')}</h4>
          <p className="text-sm text-blue-800 opacity-80">{t('blogAdmin.writing_guide_text')}</p>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/blog')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {id === 'new' ? t('blogAdmin.new_post') : t('blogAdmin.edit_post')}
            </h1>
            <p className="text-sm text-gray-500">{t('blogAdmin.blog_logic')}</p>
          </div>
        </div>
        <div className="flex gap-3 ml-auto md:ml-0">
          {id !== 'new' && (
            <Button
              variant="transparent-with-border"
              onClick={() => setShowDeleteConfirm(true)}
              className="text-red-500 hover:bg-red-50 border-gray-200"
            >
              <Trash2 size={20} />
            </Button>
          )}
          <Button onClick={handleSave} disabled={loading} className="flex items-center gap-2 px-6">
            {loading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t('blogAdmin.saving')}
              </>
            ) : (
              <>
                <Save size={20} />
                {t('blogAdmin.save')}
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Main Content Area */}
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white p-6 md:p-8 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            {/* Title Field - Premium Styling */}
            <div className="space-y-1">
              <input
                type="text"
                className="w-full text-3xl md:text-4xl font-bold border-none focus:ring-0 p-0 placeholder-gray-300"
                placeholder={t('blogAdmin.title_placeholder')}
                value={post.title || ''}
                onChange={e => handleTitleChange(e.target.value)}
              />
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span className="font-mono bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                  {publicArticleUrl}
                </span>
                {post.title && post.title.length > 70 && (
                  <span className="text-orange-500 font-medium">
                    ({t('blogAdmin.max_chars')} 70)
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {canOpenWebsitePreview && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={t('blogAdmin.go_to_preview_hint')}
                    className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/10"
                  >
                    <ExternalLink size={14} />
                    {t('blogAdmin.go_to_preview')}
                  </a>
                )}
              </div>
            </div>

            {/* Content Editor */}
            <div className="pt-4">
              <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400">
                  {t('blogAdmin.content')}
                </label>
                <div className="inline-flex w-fit items-center gap-2 rounded-full bg-secondary/[0.04] p-1">
                  <button
                    type="button"
                    onClick={() => setPreviewMode('edit')}
                    className={preview_mode_button_classes(previewMode === 'edit')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Pencil size={14} />
                      {t('blogAdmin.preview_edit')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('split')}
                    className={preview_mode_button_classes(previewMode === 'split')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Columns2 size={14} />
                      {t('blogAdmin.preview_split')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewMode('preview')}
                    className={preview_mode_button_classes(previewMode === 'preview')}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Eye size={14} />
                      {t('blogAdmin.preview_only')}
                    </span>
                  </button>
                </div>
              </div>

              <div className={previewMode === 'split' ? 'grid gap-6 xl:grid-cols-2' : ''}>
                {previewMode !== 'preview' && (
                  <RichTextEditor
                    ref={richTextEditorRef}
                    content={post.content || ''}
                    onChange={content => setPost({ ...post, content })}
                    onImageUpload={handleInlineImageUpload}
                    imageUploading={inlineImageLoading}
                    onOpenImageLibrary={() => openImageLibrary('inline')}
                  />
                )}
                {previewMode !== 'edit' && renderPreviewPanel()}
              </div>
            </div>

            {/* Excerpt */}
            <div className="pt-4">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                {t('blogAdmin.excerpt')}
              </label>
              <textarea
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none text-sm text-gray-600"
                rows={3}
                value={post.excerpt || ''}
                onChange={e => setPost({ ...post, excerpt: e.target.value })}
                placeholder={t('blogAdmin.excerpt_placeholder')}
              />
            </div>
          </div>

          {/* SEO & metadata (Expandable) */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <button
              onClick={() => setIsSeoExpanded(!isSeoExpanded)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 text-primary rounded-lg">
                  <Search size={20} />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-gray-900">{t('blogAdmin.seo_ia')}</h3>
                  <p className="text-xs text-gray-500">{t('blogAdmin.seo_hint')}</p>
                </div>
              </div>
              <ChevronDown
                className={`transition-transform ${isSeoExpanded ? 'rotate-180' : ''}`}
              />
            </button>

            {isSeoExpanded && (
              <div className="p-6 md:p-8 border-t border-gray-100 space-y-6 bg-gray-50/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputGroup
                    label={t('blogAdmin.seo_title')}
                    value={post.seo_title || ''}
                    onChange={e =>
                      setPost({ ...post, seo_title: (e.target as HTMLInputElement).value })
                    }
                    placeholder={post.title}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('blogAdmin.seo_description')}
                    </label>
                    <textarea
                      className="w-full p-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
                      rows={2}
                      value={post.seo_description || ''}
                      onChange={e => setPost({ ...post, seo_description: e.target.value })}
                    />
                  </div>
                </div>

                <div className="p-6 bg-indigo-900 text-white rounded-2xl shadow-xl space-y-3 relative overflow-hidden">
                  {/* Visual flair */}
                  <div className="absolute top-0 right-0 p-4 opacity-10">
                    <Globe size={100} />
                  </div>
                  <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-indigo-300 italic">
                    <Search size={14} />
                    {t('blogAdmin.ai_summary')}
                  </label>
                  <textarea
                    className="w-full bg-transparent border-none text-indigo-50 focus:ring-0 p-0 outline-none italic text-sm leading-relaxed"
                    rows={4}
                    value={post.ai_summary || ''}
                    onChange={e => setPost({ ...post, ai_summary: e.target.value })}
                    placeholder={t('blogAdmin.ai_summary_placeholder')}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-6">
          {/* Status & Schedule */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6">
            <h3 className="font-bold text-gray-900 flex items-center gap-2 border-b border-gray-50 pb-4">
              <Globe size={18} className="text-primary" />
              {t('blogAdmin.status')}
            </h3>

            <div className="space-y-4">
              <div>
                <select
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary appearance-none bg-no-repeat bg-[right_1rem_center] bg-[length:1rem] cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                  }}
                  value={post.status}
                  onChange={e => setPost({ ...post, status: e.target.value as BlogStatus })}
                >
                  <option value="draft">📁 {t('status.draft')}</option>
                  <option value="published">🚀 {t('status.published')}</option>
                  <option value="scheduled">🕒 {t('status.scheduled')}</option>
                </select>
              </div>

              {post.status === 'scheduled' && (
                <div className="space-y-3 p-4 bg-orange-50 rounded-xl border border-orange-100 animate-in fade-in slide-in-from-top-2">
                  <p className="text-xs font-bold text-orange-700 uppercase flex items-center gap-2">
                    <Calendar size={14} />
                    {t('blogAdmin.scheduled_at')}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white p-2 rounded-lg border border-orange-200 flex items-center gap-2">
                      <Clock size={16} className="text-orange-400" />
                      <input
                        type="datetime-local"
                        className="text-xs border-none focus:ring-0 p-0 w-full"
                        value={post.published_at?.slice(0, 16) || ''}
                        onChange={e =>
                          setPost({ ...post, published_at: new Date(e.target.value).toISOString() })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Language & linked translation */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2 border-b border-gray-50 pb-4">
              <Languages size={18} className="text-primary" />
              {t('blogAdmin.language_section')}
            </h3>
            {id === 'new' ? (
              <select
                className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary cursor-pointer"
                value={post.locale ?? 'fr'}
                onChange={e => setPost({ ...post, locale: e.target.value as BlogLocale })}
              >
                <option value="fr">Français (fr)</option>
                <option value="en">English (en)</option>
              </select>
            ) : (
              <p className="text-sm font-semibold uppercase tracking-wide text-secondary">
                {post.locale === 'en' ? 'English (en)' : 'Français (fr)'}
              </p>
            )}
            {post.translation_group_id && (
              <div className="space-y-2">
                {siblingPostId ? (
                  <Button
                    type="button"
                    variant="transparent-with-border"
                    className="w-full text-sm"
                    onClick={() => navigate(`/admin/blog/${siblingPostId}`)}
                  >
                    {t('blogAdmin.edit_other_locale', {
                      locale: otherLocale.toUpperCase(),
                    })}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="transparent-with-border"
                    className="w-full text-sm"
                    onClick={() =>
                      navigate(
                        `/admin/blog/new?translation_group=${post.translation_group_id}&locale=${otherLocale}`
                      )
                    }
                  >
                    {t('blogAdmin.create_other_locale', {
                      locale: otherLocale.toUpperCase(),
                    })}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Author Box */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2 border-b border-gray-50 pb-4">
              <User size={18} className="text-primary" />
              {t('blogAdmin.author')}
            </h3>

            <div className="space-y-2">
              <select
                className="w-full p-3 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary appearance-none bg-no-repeat bg-[right_1rem_center] bg-[length:1rem] cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                }}
                value={post.author_id || ''}
                onChange={e => setPost({ ...post, author_id: e.target.value })}
              >
                <option value="">{t('blogAdmin.select_author')}</option>
                {authors.map(author => (
                  <option key={author.id} value={author.id}>
                    {author.first_name} {author.last_name} ({author.role})
                  </option>
                ))}
              </select>
              {profile && post.author_id === profile.id && (
                <p className="text-[10px] text-primary font-bold uppercase tracking-wider pl-1 italic">
                  {t('blogAdmin.you_are_selected')}
                </p>
              )}
            </div>
          </div>

          {/* Categories Box */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <ChevronDown size={18} className="text-primary" />
              {t('blogAdmin.categories')}
            </h3>
            <div className="max-h-48 overflow-y-auto pr-2 space-y-1 custom-scrollbar">
              {categories.map(cat => (
                <label
                  key={cat.id}
                  className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors group"
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary focus:ring-primary w-4 h-4"
                    checked={selectedCategoryIds.includes(cat.id)}
                    onChange={() => toggleCategory(cat.id)}
                  />
                  <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                    {cat.name}
                  </span>
                </label>
              ))}
            </div>
            <button
              onClick={handleQuickCategory}
              className="text-xs text-primary font-bold flex items-center gap-1 hover:underline"
            >
              <Plus size={14} />
              {t('blogAdmin.create_category')}
            </button>
          </div>

          {/* Tags Box */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Plus size={18} className="text-primary" />
              {t('blogAdmin.tags')}
            </h3>
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedTagIds.map(id => {
                const tag = tags.find(t => t.id === id);
                if (!tag) return null;
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"
                  >
                    {tag.name}
                    <X
                      size={12}
                      className="cursor-pointer hover:text-red-500"
                      onClick={() => toggleTag(id)}
                    />
                  </span>
                );
              })}
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder={t('blogAdmin.add_tag')}
                className="w-full p-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const val = (e.target as HTMLInputElement).value;
                    if (val) {
                      handleQuickTag(val);
                      (e.target as HTMLInputElement).value = '';
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <ImageIcon size={18} className="text-primary" />
              {t('blogAdmin.featured_image')}
            </h3>
            {post.featured_image_url ? (
              <div className="mb-4 relative group overflow-hidden rounded-xl bg-gray-100">
                <img
                  src={post.featured_image_url}
                  alt="Featured"
                  className={`w-full aspect-[1200/630] object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-50' : 'opacity-100'}`}
                />
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 size={32} className="text-primary animate-spin" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      openImageLibrary('featured');
                    }}
                    disabled={imageLoading}
                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors text-gray-900"
                    title={t('blogAdmin.choose_from_library')}
                  >
                    <Images size={20} />
                  </button>
                  <label className="p-2 bg-white rounded-full cursor-pointer hover:bg-gray-100 transition-colors text-gray-900">
                    <ImageIcon size={20} />
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleImageUpload}
                      accept="image/*"
                      disabled={imageLoading}
                    />
                  </label>
                  <button
                    onClick={() => setPost({ ...post, featured_image_url: '' })}
                    disabled={imageLoading}
                    className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors text-white"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <label
                  className={`cursor-pointer flex flex-col items-center justify-center aspect-[16/9] border-2 border-dashed border-gray-200 rounded-xl p-4 transition-all group ${imageLoading ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary hover:bg-primary/5'}`}
                >
                  <div className="p-3 bg-gray-50 rounded-full text-gray-400 group-hover:text-primary group-hover:bg-white transition-all">
                    {imageLoading ? (
                      <Loader2 size={24} className="animate-spin" />
                    ) : (
                      <Plus size={24} />
                    )}
                  </div>
                  <span className="text-xs font-bold text-gray-500 mt-3">
                    {imageLoading ? t('blogAdmin.uploading') : t('blogAdmin.upload_image')}
                  </span>
                  <p className="text-[10px] text-gray-400 mt-1">{t('blogAdmin.required_size')}</p>
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleImageUpload}
                    accept="image/*"
                    disabled={imageLoading}
                  />
                </label>
                <Button
                  type="button"
                  variant="transparent-with-border"
                  className="w-full justify-center gap-2 border-gray-200"
                  onClick={() => openImageLibrary('featured')}
                  disabled={imageLoading}
                >
                  <Images size={18} />
                  {t('blogAdmin.choose_from_library')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <BlogImageLibraryModal
        isOpen={imageLibraryOpen}
        onClose={() => setImageLibraryOpen(false)}
        onPick={handleImageLibraryPick}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title={t('blogAdmin.confirm_delete')}
        message={
          t('blogAdmin.confirm_delete_hint') ||
          "Cette action est irréversible. L'article sera définitivement supprimé."
        }
        confirmText={t('blogAdmin.delete')}
        cancelText={t('blogAdmin.cancel')}
      />
    </div>
  );
};

export default BlogEditor;
